import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCcw, Share2, Download, Copy, Check, Image as ImageIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function CardTile({ card, onRegenerate, regeneratingSlug }) {
  const [copied, setCopied] = useState(null); // 'og' | 'product' | null

  const copy = async (kind, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  // Preview via preview backend URL so we see the actual file even if prod
  // hasn't been redeployed yet.
  const previewSrc = `${card.og_url}?t=${card.generated_at || ''}`;

  const isRegenerating = regeneratingSlug === card.slug;

  return (
    <div
      className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden flex flex-col"
      data-testid={`og-card-tile-${card.slug}`}
    >
      <a
        href={previewSrc}
        target="_blank"
        rel="noreferrer"
        className="block aspect-[1200/630] bg-black relative overflow-hidden"
        title="Open full-size in a new tab"
      >
        {card.exists ? (
          <img
            src={previewSrc}
            alt={`${card.name} share card`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-500 text-sm">
            <ImageIcon className="w-6 h-6 mr-2" /> Not generated yet
          </div>
        )}
      </a>

      <div className="p-3 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-white font-semibold truncate">{card.name}</div>
            <div className="text-xs text-neutral-500 truncate">/{card.slug}</div>
          </div>
          <div className="text-xs text-neutral-400 shrink-0">
            {card.is_subscription ? `$${card.price}/mo` : `$${card.price}`}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            size="sm"
            variant="outline"
            className="text-xs border-white/15 bg-white/[0.03] hover:bg-white/[0.06]"
            onClick={() => copy('og', card.public_og_url)}
            data-testid={`og-copy-image-${card.slug}`}
          >
            {copied === 'og' ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
            Image URL
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-xs border-white/15 bg-white/[0.03] hover:bg-white/[0.06]"
            onClick={() => copy('product', card.product_url)}
            data-testid={`og-copy-product-${card.slug}`}
          >
            {copied === 'product' ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
            Product URL
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <a
            href={previewSrc}
            download={`${card.slug}-share.jpg`}
            className="inline-flex items-center justify-center h-8 rounded-md text-xs font-medium border border-white/15 bg-white/[0.03] hover:bg-white/[0.06] text-white"
            data-testid={`og-download-${card.slug}`}
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Download
          </a>
          <Button
            size="sm"
            className="text-xs bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/30"
            onClick={() => onRegenerate(card.slug)}
            disabled={isRegenerating}
            data-testid={`og-regenerate-${card.slug}`}
          >
            {isRegenerating ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <RefreshCcw className="w-3.5 h-3.5 mr-1" />
            )}
            Regenerate
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminShareCards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratingSlug, setRegeneratingSlug] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/products/og-cards`, { headers: authHeaders() });
      setCards(data.cards || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load share cards');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const regenerateOne = async (slug) => {
    setRegeneratingSlug(slug);
    try {
      await axios.post(
        `${API_URL}/api/admin/products/og-cards/regenerate?slug=${encodeURIComponent(slug)}`,
        {},
        { headers: authHeaders() },
      );
      toast.success(`Regenerated ${slug}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Regenerate failed');
    } finally {
      setRegeneratingSlug(null);
    }
  };

  const regenerateAll = async () => {
    if (!window.confirm(`Regenerate share cards for all ${cards.length} products? Takes ~5-10s.`)) return;
    setRegenerating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/products/og-cards/regenerate`,
        {},
        { headers: authHeaders() },
      );
      toast.success(`Regenerated ${data.regenerated}/${data.total}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk regenerate failed');
    } finally {
      setRegenerating(false);
    }
  };

  const missing = cards.filter((c) => !c.exists).length;

  return (
    <AdminLayout>
      <div className="p-6 md:p-8 max-w-[1400px] mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Share2 className="w-7 h-7 text-[#00F0FF]" /> Product Share Cards
            </h1>
            <p className="text-sm text-neutral-400 mt-1 max-w-2xl">
              1200×630 OG images auto-wired into <code className="text-[#00F0FF]">/product/:slug</code> for LinkedIn, X, Slack,
              iMessage, and email previews. Download to attach to LinkedIn posts, X threads, or cold-email drips.
            </p>
            <p className="text-xs text-amber-400/80 mt-2 max-w-2xl">
              ⚠️ For LinkedIn / X / iMessage to actually render these on link paste, the site needs server-side rendering or a
              prerender proxy on production. Meta tags are set client-side today — works for Discord and modern Google,
              limited on other platforms until SSR ships.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              className="border-white/15 bg-white/[0.03] hover:bg-white/[0.06]"
              data-testid="og-refresh-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCcw className="w-4 h-4 mr-1.5" />}
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={regenerateAll}
              disabled={regenerating || loading}
              className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
              data-testid="og-regenerate-all-btn"
            >
              {regenerating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCcw className="w-4 h-4 mr-1.5" />}
              Regenerate all ({cards.length})
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
            <div className="text-xs text-neutral-400 uppercase tracking-wider">Total products</div>
            <div className="text-2xl font-bold text-white mt-1">{cards.length}</div>
          </div>
          <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
            <div className="text-xs text-neutral-400 uppercase tracking-wider">Cards generated</div>
            <div className="text-2xl font-bold text-[#00FF94] mt-1">{cards.length - missing}</div>
          </div>
          <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
            <div className="text-xs text-neutral-400 uppercase tracking-wider">Missing</div>
            <div className={`text-2xl font-bold mt-1 ${missing > 0 ? 'text-amber-400' : 'text-neutral-500'}`}>{missing}</div>
          </div>
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-neutral-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading share cards…
          </div>
        ) : cards.length === 0 ? (
          <div className="py-16 text-center text-neutral-400">
            No products with slugs yet. Publish a product to generate its share card.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {cards.map((card) => (
              <CardTile
                key={card.slug}
                card={card}
                onRegenerate={regenerateOne}
                regeneratingSlug={regeneratingSlug}
              />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
